import { POSTHOG_SCHEMA_VERSION } from '@/lib/posthogIdentity';
import { trackEvent } from '@/lib/trackEvent';
import { getMostVisibleSection } from '@/lib/reportSectionVisibility';

export type ShareMethod =
  | 'whatsapp'
  | 'twitter'
  | 'linkedin'
  | 'email'
  | 'copy_link'
  | 'native_share'
  | 'other';

export type ShareScope = 'report' | 'content';

const normalizeShareMethod = (value?: string | null): ShareMethod => {
  const normalized = (value || '').trim().toLowerCase();
  if (normalized === 'whatsapp') return 'whatsapp';
  if (normalized === 'twitter' || normalized === 'x') return 'twitter';
  if (normalized === 'linkedin') return 'linkedin';
  if (normalized === 'email' || normalized === 'mail') return 'email';
  if (normalized === 'copy_link' || normalized === 'copy-link' || normalized === 'copy') {
    return 'copy_link';
  }
  if (normalized === 'native_share' || normalized === 'native' || normalized === 'share') {
    return 'native_share';
  }
  return 'other';
};

const normalizeShareScope = (value?: string | null): ShareScope => {
  const normalized = (value || '').trim().toLowerCase();
  if (normalized === 'content') return 'content';
  return 'report';
};

const resolveVisibleSectionName = (
  shareButton: HTMLElement,
  root: ParentNode
): string => {
  const closestSection = shareButton.closest('[data-report-section]') as HTMLElement | null;
  const visible = getMostVisibleSection(root);
  return (
    closestSection?.dataset.reportSectionName ||
    closestSection?.dataset.reportSection ||
    visible?.name ||
    'unknown'
  );
};

const resolveShareScope = (shareButton: HTMLElement): ShareScope => {
  const explicit =
    shareButton.dataset.shareScope ||
    (shareButton.closest('[data-share-scope]') as HTMLElement | null)?.dataset
      .shareScope;
  return normalizeShareScope(explicit);
};

const resolveSpecialName = (
  shareButton: HTMLElement,
  shareScope: ShareScope
): string | undefined => {
  if (shareScope !== 'content') return undefined;

  const fromButton = shareButton.dataset.reportSpecialName?.trim();
  if (fromButton) return fromButton;

  const host = shareButton.closest(
    '[data-report-special-name]'
  ) as HTMLElement | null;
  return host?.dataset.reportSpecialName?.trim() || undefined;
};

const resolveSharedUrl = (
  shareButton: HTMLElement,
  shareScope: ShareScope
): string | undefined => {
  if (shareScope !== 'content') return undefined;

  const fromButton = shareButton.dataset.sharedUrl?.trim();
  if (fromButton) return fromButton;

  const host = shareButton.closest('[data-shared-url]') as HTMLElement | null;
  return host?.dataset.sharedUrl?.trim() || undefined;
};

export const trackReportShared = (input: {
  reportId: string;
  shareMethod: string;
  sectionName?: string | null;
  shareScope?: ShareScope | string | null;
  specialName?: string | null;
  sharedUrl?: string | null;
}): boolean => {
  const shareScope = normalizeShareScope(input.shareScope);
  const properties: Record<string, unknown> = {
    report_id: input.reportId,
    schema_version: POSTHOG_SCHEMA_VERSION,
    share_method: normalizeShareMethod(input.shareMethod),
    section_name: input.sectionName || 'unknown',
    share_scope: shareScope,
  };

  if (shareScope === 'content' && input.specialName) {
    properties.special_name = input.specialName;
  }
  if (shareScope === 'content' && input.sharedUrl) {
    properties.shared_url = input.sharedUrl;
  }

  return trackEvent('report_shared', properties);
};

export const trackContentCopied = (input: {
  reportId: string;
  sectionName?: string | null;
  copiedLength: number;
}): boolean => {
  const properties: Record<string, unknown> = {
    report_id: input.reportId,
    schema_version: POSTHOG_SCHEMA_VERSION,
    copied_length: Math.max(0, input.copiedLength),
  };
  if (input.sectionName) {
    properties.section_name = input.sectionName;
  }
  return trackEvent('content_copied', properties);
};

const resolveSectionNameFromNode = (node: Node | null): string | undefined => {
  if (!node) return undefined;
  const element =
    node instanceof Element ? node : (node.parentElement as HTMLElement | null);
  const section = element?.closest?.('[data-report-section]') as HTMLElement | null;
  return (
    section?.dataset.reportSectionName ||
    section?.dataset.reportSection ||
    undefined
  );
};

export const startReportAdvocacyTracking = (
  reportId: string,
  root: ParentNode = document
): (() => void) => {
  if (typeof document === 'undefined' || !reportId) {
    return () => undefined;
  }

  const handleShareClick = (event: Event) => {
    const target = event.target as HTMLElement | null;
    const shareButton = target?.closest?.(
      '[data-share-method], [data-report-share]'
    ) as HTMLElement | null;
    if (!shareButton || !root.contains(shareButton)) return;

    const shareMethod =
      shareButton.dataset.shareMethod ||
      shareButton.dataset.reportShare ||
      'other';
    const shareScope = resolveShareScope(shareButton);

    trackReportShared({
      reportId,
      shareMethod,
      sectionName: resolveVisibleSectionName(shareButton, root),
      shareScope,
      specialName: resolveSpecialName(shareButton, shareScope),
      sharedUrl: resolveSharedUrl(shareButton, shareScope),
    });
  };

  const handleCopy = () => {
    const selection = window.getSelection();
    const text = selection?.toString() || '';
    if (!text) return;

    const anchorNode = selection?.anchorNode ?? null;
    const sectionName =
      resolveSectionNameFromNode(anchorNode) ||
      getMostVisibleSection(root)?.name ||
      undefined;

    trackContentCopied({
      reportId,
      sectionName,
      copiedLength: text.length,
    });
  };

  root.addEventListener('click', handleShareClick, true);
  document.addEventListener('copy', handleCopy);

  return () => {
    root.removeEventListener('click', handleShareClick, true);
    document.removeEventListener('copy', handleCopy);
  };
};
